import { useEffect, useState } from "react";
import api, { fmtErr } from "../../lib/api";
import { toast } from "sonner";
import { PageHeader, Modal, inputCls, btnTurq, btnGhost, EmptyState } from "../../components/bits";
import { Plus, Pencil, Trash2, Settings2, School, ListChecks, X } from "lucide-react";

const emptyForm = { name: "", email: "", phone: "", sectors: [], password: "" };
const ALL_SCHOOLS = "جميع المدارس";

export default function AdminSupervisors() {
  const [sups, setSups] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [schools, setSchools] = useState([]);
  const [busy, setBusy] = useState(false);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [newSector, setNewSector] = useState("");
  const [notes, setNotes] = useState([]);
  const [assignQ, setAssignQ] = useState("");

  const load = () => Promise.all([
    api.get("/admin/supervisors").then((r) => setSups(r.data)),
    api.get("/admin/sectors").then((r) => setSectors(r.data.sectors)),
  ]).catch((e) => toast.error(fmtErr(e)));

  useEffect(() => {
    load();
    api.get("/admin/schools").then((r) => setSchools(r.data.map((x) => x.school || x))).catch(() => {});
  }, []);

  const schoolName = (id) => schools.find((s) => s.id === id)?.name || id;

  const toggleSector = (s) => setForm((f) => ({
    ...f, sectors: f.sectors.includes(s) ? f.sectors.filter((x) => x !== s) : [...f.sectors, s],
  }));

  const save = async () => {
    setBusy(true);
    try {
      if (modal.mode === "edit") {
        await api.put(`/admin/supervisors/${modal.sup.id}`, form);
        toast.success("حُدّثت بيانات المشرفة");
      } else {
        await api.post("/admin/supervisors", form);
        toast.success("أُنشئ حساب المشرفة — كلمة المرور: رقم الجوال (ما لم تُحدد غير ذلك)");
      }
      setModal(null);
      setForm(emptyForm);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (sup) => {
    setBusy(true);
    try {
      await api.delete(`/admin/supervisors/${sup.id}`);
      toast.success("حُذف حساب المشرفة");
      setModal(null);
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const assign = async (supId, schoolId) => {
    try {
      await api.post(`/admin/supervisors/${supId}/assign-school`, { school_id: schoolId });
      toast.success("أُسندت المدرسة للمشرفة");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const unassign = async (supId, schoolId) => {
    try {
      await api.delete(`/admin/supervisors/${supId}/assign-school/${schoolId}`);
      toast.success("أُلغي الإسناد");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const addSector = async () => {
    if (!newSector.trim()) return;
    try {
      await api.post("/admin/sectors", { name: newSector.trim() });
      setNewSector("");
      toast.success("أُضيف القطاع");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const delSector = async (name) => {
    try {
      await api.delete(`/admin/sectors/${encodeURIComponent(name)}`);
      toast.success("حُذف القطاع");
      load();
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const openNotes = async (sup) => {
    setModal({ mode: "notes", sup });
    setNotes([]);
    try {
      const { data } = await api.get(`/admin/supervisors/${sup.id}/notes`);
      setNotes(data);
    } catch (e) {
      toast.error(fmtErr(e));
    }
  };

  const assignCandidates = schools.filter((s) =>
    assignQ && s.name.includes(assignQ) && !(modal?.sup.manual_school_ids || []).includes(s.id)).slice(0, 8);

  return (
    <div data-testid="admin-supervisors-page" className="space-y-6">
      <PageHeader title="المشرفون — فريق المتابعة" sub="حسابات المشرفات وقطاعاتهن — ترتبط المدارس تلقائياً بحقل القطاع، مع إمكانية الإسناد اليدوي"
        actions={<>
          <button data-testid="sectors-manage-btn" onClick={() => setModal({ mode: "sectors" })} className={btnGhost}><Settings2 className="w-4 h-4" /> إدارة القطاعات</button>
          <button data-testid="supervisor-add-btn" onClick={() => { setForm(emptyForm); setModal({ mode: "add" }); }} className={btnTurq}><Plus className="w-4 h-4" /> إضافة مشرفة</button>
        </>} />

      {sups.length === 0 ? (
        <EmptyState testid="supervisors-empty" text="لا توجد مشرفات بعد — أضف أول مشرفة وحدد قطاعاتها" />
      ) : (
        <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
          {sups.map((s) => (
            <div key={s.id} data-testid={`supervisor-card-${s.id.slice(0, 8)}`} className="bg-surface rounded-2xl border border-brd shadow-md p-5 space-y-3">
              <div>
                <div className="font-head font-bold text-[15px]">{s.name}</div>
                <div className="text-[11px] text-mutext" dir="ltr">{s.email} · {s.phone}</div>
              </div>
              <div className="flex gap-1 flex-wrap" data-testid={`supervisor-sectors-${s.id.slice(0, 8)}`}>
                {(s.sectors || []).length === 0 && <span className="text-[10px] text-coral-600 font-bold">بلا قطاعات — لن تظهر لها مدارس</span>}
                {(s.sectors || []).map((sec) => (
                  <span key={sec} className={`text-[10px] font-bold rounded-full px-2.5 py-1 ${sec === ALL_SCHOOLS ? "bg-coral-100 text-coral-900 dark:bg-surface2 dark:text-coral-300" : "bg-beige-600 dark:bg-surface2"}`}>{sec}</span>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div className="rounded-xl bg-beige-300 dark:bg-surface2 p-2.5 text-center">
                  <div data-testid={`supervisor-schools-${s.id.slice(0, 8)}`} className="font-head font-black text-base">{s.schools_count}</div>
                  <div className="text-[10px] text-mutext">مدرسة</div>
                </div>
                <div className="rounded-xl bg-beige-300 dark:bg-surface2 p-2.5 text-center">
                  <div data-testid={`supervisor-hours-${s.id.slice(0, 8)}`} className="font-head font-black text-base">{s.hours}</div>
                  <div className="text-[10px] text-mutext">ساعة</div>
                </div>
                <div className="rounded-xl bg-beige-300 dark:bg-surface2 p-2.5 text-center">
                  <div data-testid={`supervisor-notes-count-${s.id.slice(0, 8)}`} className="font-head font-black text-base">{s.notes_count}</div>
                  <div className="text-[10px] text-mutext">ملاحظة</div>
                </div>
              </div>
              <div className="flex gap-1.5">
                <button data-testid={`supervisor-edit-${s.id.slice(0, 8)}`} onClick={() => { setForm({ name: s.name, email: s.email, phone: s.phone || "", sectors: s.sectors || [], password: "" }); setModal({ mode: "edit", sup: s }); }}
                  className="flex-1 rounded-xl bg-turq-100 dark:bg-surface2 text-turq-900 dark:text-turq-300 text-[11px] font-bold py-2 flex items-center justify-center gap-1 hover:bg-turq-300/60 transition-colors">
                  <Pencil className="w-3.5 h-3.5" /> تعديل
                </button>
                <button data-testid={`supervisor-assign-${s.id.slice(0, 8)}`} onClick={() => { setAssignQ(""); setModal({ mode: "assign", sup: s }); }}
                  className="flex-1 rounded-xl bg-turq-100 dark:bg-surface2 text-turq-900 dark:text-turq-300 text-[11px] font-bold py-2 flex items-center justify-center gap-1 hover:bg-turq-300/60 transition-colors">
                  <School className="w-3.5 h-3.5" /> توزيع يدوي
                </button>
                <button data-testid={`supervisor-notes-${s.id.slice(0, 8)}`} onClick={() => openNotes(s)}
                  className="flex-1 rounded-xl bg-turq-100 dark:bg-surface2 text-turq-900 dark:text-turq-300 text-[11px] font-bold py-2 flex items-center justify-center gap-1 hover:bg-turq-300/60 transition-colors">
                  <ListChecks className="w-3.5 h-3.5" /> الملاحظات
                </button>
                <button data-testid={`supervisor-delete-${s.id.slice(0, 8)}`} onClick={() => setModal({ mode: "delete", sup: s })}
                  className="w-9 rounded-xl bg-coral-100 dark:bg-surface2 flex items-center justify-center hover:scale-105 transition-transform">
                  <Trash2 className="w-3.5 h-3.5 text-coral-900 dark:text-coral-300" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal open={modal?.mode === "add" || modal?.mode === "edit"} onClose={() => setModal(null)}
        title={modal?.mode === "edit" ? "تعديل بيانات المشرفة" : "إضافة مشرفة"} testid="supervisor-form-modal">
        <div className="space-y-3">
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الاسم *</label>
            <input data-testid="supervisor-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={inputCls} />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">البريد الإلكتروني (للدخول) *</label>
            <input data-testid="supervisor-email-input" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className={inputCls} dir="ltr" />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">الجوال (كلمة المرور الافتراضية) *</label>
            <input data-testid="supervisor-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} dir="ltr" placeholder="05xxxxxxxx" />
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">القطاعات المشرَف عليها *</label>
            <div data-testid="supervisor-sectors-picker" className="flex gap-1.5 flex-wrap">
              {[ALL_SCHOOLS, ...sectors].map((sec) => (
                <button key={sec} type="button" data-testid={`supervisor-sector-chip-${sec}`} onClick={() => toggleSector(sec)}
                  className={`rounded-full px-3 py-1.5 text-[11px] font-bold transition-all ${form.sectors.includes(sec)
                    ? "bg-turq-600 text-white shadow-md" : "bg-bg text-mutext border border-brd hover:scale-105"}`}>
                  {sec}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-mutext block mb-1.5">كلمة مرور مخصصة (اتركها فارغة = رقم الجوال)</label>
            <input data-testid="supervisor-password-input" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className={inputCls} dir="ltr" />
          </div>
          <button data-testid="supervisor-save-btn" onClick={save} disabled={busy} className={`${btnTurq} w-full justify-center`}>
            {busy ? "جارٍ الحفظ …" : "حفظ"}
          </button>
        </div>
      </Modal>

      <Modal open={modal?.mode === "assign"} onClose={() => setModal(null)} title={`التوزيع اليدوي — ${modal?.sup.name || ""}`} testid="supervisor-assign-modal">
        <div className="space-y-3">
          <p className="text-[11px] text-mutext">مدارس قطاعات المشرفة تظهر لها تلقائياً. هنا تُسند مدارس إضافية خارج قطاعاتها بشكل يدوي.</p>
          <div data-testid="assign-current-list" className="space-y-1.5">
            {(modal?.sup.manual_school_ids || []).length === 0 && <div className="text-center text-mutext text-xs py-3">لا توجد إسنادات يدوية</div>}
            {(modal?.sup.manual_school_ids || []).map((id) => (
              <div key={id} className="flex items-center justify-between rounded-xl border border-brd bg-bg px-3 py-2">
                <span className="text-[13px] font-bold">{schoolName(id)}</span>
                <button data-testid={`assign-remove-${id.slice(0, 8)}`} onClick={() => unassign(modal.sup.id, id)}
                  className="text-coral-900 dark:text-coral-300 hover:scale-110 transition-transform"><X className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <input data-testid="assign-search-input" value={assignQ} onChange={(e) => setAssignQ(e.target.value)}
            placeholder="ابحث باسم المدرسة لإسنادها…" className={inputCls} />
          <div className="space-y-1.5 max-h-56 overflow-auto">
            {assignCandidates.map((s) => (
              <button key={s.id} data-testid={`assign-add-${s.id.slice(0, 8)}`} onClick={() => assign(modal.sup.id, s.id)}
                className="w-full text-right rounded-xl border border-brd bg-surface px-3 py-2 text-[13px] font-bold hover:border-turq-600 transition-colors">
                {s.name} <span className="text-[10px] text-mutext font-normal">· {s.stage}{s.sector ? ` · ${s.sector}` : ""}</span>
              </button>
            ))}
          </div>
        </div>
      </Modal>

      <Modal open={modal?.mode === "sectors"} onClose={() => setModal(null)} title="إدارة القطاعات (المحافظات)" testid="sectors-modal">
        <div className="space-y-3">
          <div className="flex gap-2">
            <input data-testid="sector-name-input" value={newSector} onChange={(e) => setNewSector(e.target.value)}
              placeholder="اسم القطاع الجديد…" className={inputCls} />
            <button data-testid="sector-add-btn" onClick={addSector} className={btnTurq}><Plus className="w-4 h-4" /> إضافة</button>
          </div>
          <div className="flex gap-1.5 flex-wrap" data-testid="sectors-list">
            {sectors.map((s) => (
              <span key={s} className="inline-flex items-center gap-1.5 text-[11px] font-bold bg-beige-600 dark:bg-surface2 rounded-full px-3 py-1.5">
                {s}
                <button data-testid={`sector-delete-${s}`} onClick={() => delSector(s)} className="text-coral-900 dark:text-coral-300 hover:scale-110 transition-transform"><X className="w-3 h-3" /></button>
              </span>
            ))}
          </div>
          <p className="text-[11px] text-mutext">«جميع المدارس» خيار خاص يمنح المشرفة كل المدارس — لا يحتاج قطاعاً.</p>
        </div>
      </Modal>

      <Modal open={modal?.mode === "notes"} onClose={() => setModal(null)} title={`ملاحظات المتابعة — ${modal?.sup.name || ""}`} testid="supervisor-notes-modal">
        <div className="space-y-2 max-h-96 overflow-auto">
          {notes.length === 0 && <div data-testid="supervisor-notes-empty" className="text-center text-mutext text-sm py-8">لا توجد ملاحظات مسجلة من هذه المشرفة بعد</div>}
          {notes.map((n) => (
            <div key={n.id} data-testid={`admin-note-item-${n.id.slice(0, 8)}`} className="rounded-xl border border-brd bg-bg p-3">
              <div className="text-[13px] font-bold leading-relaxed">{n.note}</div>
              <div className="text-[10px] text-mutext mt-1.5">{n.school_name} · {new Date(n.created_at).toLocaleDateString("ar-SA")}</div>
            </div>
          ))}
        </div>
      </Modal>

      <Modal open={modal?.mode === "delete"} onClose={() => setModal(null)} title="حذف حساب المشرفة" testid="supervisor-delete-modal">
        <div className="space-y-3">
          <p className="text-sm">حذف حساب <b>{modal?.sup.name}</b> نهائياً؟ ستفقد الوصول فوراً وتُلغى إسناداتها اليدوية (تبقى ملاحظاتها محفوظة).</p>
          <div className="flex gap-2">
            <button data-testid="supervisor-delete-confirm" onClick={() => remove(modal.sup)} disabled={busy}
              className="flex-1 rounded-xl bg-coral-600 text-white text-sm font-bold py-2.5 hover:bg-coral-900 transition-colors">
              {busy ? "جارٍ الحذف …" : "نعم، احذف"}
            </button>
            <button data-testid="supervisor-delete-cancel" onClick={() => setModal(null)} className={`${btnGhost} flex-1 justify-center`}>إلغاء</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
